// src/modules/tickets/pages/TicketDetailPage.tsx
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  Divider,
  Avatar,
  Skeleton,
  useTheme,
  useMediaQuery,
  Alert,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Assignment as TicketIcon,
  Edit as EditIcon,
  ReceiptLong as InvoiceIcon,
  Print as PrintIcon,
  Label as LabelIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import api from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";
import { useProtectedRoute } from "../../../hooks/useProtectedRoute";

export default function TicketDetailPage() {
  useProtectedRoute();
  const { token } = useAuth();
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const { id } = useParams();
  const navigate = useNavigate();

  const [ticket, setTicket] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [serverError, setServerError] = useState<string | null>(null);

  const heroGradient = useMemo(
    () =>
      `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 55%, #0f172a 100%)`,
    [theme]
  );
  const surfaceBorder = `1px solid ${
    theme.palette.mode === "light" ? "#e5e7eb" : theme.palette.divider
  }`;
  const clientIdForInvoice =
    ticket?.clientId && typeof ticket.clientId === "object"
      ? ticket.clientId._id
      : ticket?.clientId;


  useEffect(() => {
    let mounted = true;
    const fetchTicket = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/tickets/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!mounted) return;
        setTicket(res.data);
      } catch (e) {
        if (!mounted) return;
        setServerError("Failed to load ticket.");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchTicket();
    return () => {
      mounted = false;
    };
  }, [id, token]);

  const statusChip = (status?: string) => {
    const s = (status || "").toLowerCase();
    let bg = "#6b7280";
    if (s === "open" || s === "in-progress") bg = "#10b981";
    else if (s === "pending" || s === "on-hold") bg = "#f59e0b";
    else if (s === "completed" || s === "closed") bg = "#3b82f6";
    return (
      <Chip
        size="small"
        label={status || "—"}
        sx={{ fontWeight: 700, textTransform: "capitalize", color: "#fff", bgcolor: bg }}
      />
    );
  };

  const safe = (v: any) => (v === null || v === undefined || v === "" ? "—" : v);
  const fmtDate = (iso?: string) => (iso ? new Date(iso).toLocaleDateString() : "—");
  const fmtDateTime = (iso?: string) => (iso ? new Date(iso).toLocaleString() : "—");

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      sx={{ p: { xs: 2, md: 3 }, pb: { xs: 1, md: 2 } }}
    >
      {/* HERO / HEADER */}
      <Card
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 3,
          overflow: "hidden",
          border: surfaceBorder,
          background: heroGradient,
          color: "white",
        }}
      >
        <CardContent sx={{ py: { xs: 2.25, md: 2.75 } }}>
          <Grid container spacing={2} alignItems="center" justifyContent="space-between">
            <Grid item xs={12} md="auto">
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
                <Avatar
                  sx={{
                    bgcolor: "rgba(255,255,255,0.15)",
                    border: "1px solid rgba(255,255,255,0.18)",
                    width: 44,
                    height: 44,
                    backdropFilter: "blur(4px)",
                  }}
                >
                  <TicketIcon />
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant={isSmDown ? "h5" : "h4"}
                    sx={{ fontWeight: 900, lineHeight: 1.15 }}
                    noWrap
                  >
                    {loading ? "Ticket" : `Ticket #${ticket?.ticketNumber || ticket?._id}`}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }} noWrap>
                    {loading
                      ? "Loading details…"
                      : `${safe(ticket?.clientName)} • ${safe(ticket?.deviceType)} ${safe(
                          ticket?.brand
                        )} ${safe(ticket?.model)}`}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Action bar */}
            <Grid item xs={12} md>
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  flexWrap: "wrap",
                  justifyContent: { xs: "stretch", md: "flex-end" },
                  mt: { xs: 1, md: 0 },
                }}
              >
                <Button
                  variant="text"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate("/tickets")}
                  sx={{ color: "white", fontWeight: 800 }}
                >
                  Back to Tickets
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<EditIcon />}
                  onClick={() => navigate(`/tickets/${ticket?._id}/edit`)}
                  disabled={loading}
                  sx={{ fontWeight: 800 }}
                >
                  Edit
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<InvoiceIcon />}
                  onClick={() =>
                    navigate(
                      `/invoices/create?ticketId=${ticket?._id}&clientId=${clientIdForInvoice}`
                    )
                  }
                  disabled={loading}
                  sx={{ fontWeight: 800 }}
                >
                  Create Invoice
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<PrintIcon />}
                  onClick={() => navigate(`/tickets/${ticket?._id}/print`)}
                  disabled={loading}
                  sx={{ fontWeight: 800, bgcolor: "rgba(255,255,255,0.08)" }}
                >
                  Print
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<LabelIcon />}
                  onClick={() => navigate(`/tickets/${ticket?._id}/label`)}
                  disabled={loading}
                  sx={{ fontWeight: 800, bgcolor: "rgba(255,255,255,0.08)" }}
                >
                  Label
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {serverError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {serverError}
        </Alert>
      )}

      {/* CONTENT */}
      <Card elevation={0} sx={{ borderRadius: 3, border: surfaceBorder }}>
        <CardContent>
          {loading ? (
            <Box>
              <Skeleton height={28} sx={{ mb: 2 }} />
              <Skeleton height={28} sx={{ mb: 2 }} />
              <Skeleton height={28} sx={{ mb: 2 }} />
              <Skeleton height={120} />
            </Box>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Client
                </Typography>
                <Typography>{`${safe(ticket.clientName)}${
                  ticket.clientPhone ? ` (${ticket.clientPhone})` : ""
                }`}</Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Assigned Technician
                </Typography>
                <Typography>
                  {ticket?.assignedTo?.name || ticket?.assignedTo?.email || 'Unassigned'}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Created By
                </Typography>
                <Typography>
                  {ticket.createdBy?.fullName ||
                    ticket.createdBy?.name ||
                    ticket.createdBy?.email ||
                    "—"}
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Job Type
                </Typography>
                <Typography>{safe(ticket.jobType)}</Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Device
                </Typography>
                <Typography>{`${safe(ticket.deviceType)} • ${safe(ticket.brand)} • ${safe(
                  ticket.model
                )}`}</Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Serial / IMEI
                </Typography>
                <Typography>{safe(ticket.serialOrImei)}</Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Status
                </Typography>
                {statusChip(ticket.status)}
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Estimated Completion
                </Typography>
                <Typography>{fmtDate(ticket.estimatedFinishDate)}</Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Created At
                </Typography>
                <Typography>{fmtDateTime(ticket.createdAt)}</Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="subtitle2" color="text.secondary">
                  Diagnostic Notes
                </Typography>
                <Box
                  sx={{
                    mt: 1,
                    p: 2,
                    borderRadius: 2,
                    border: `1px dashed ${theme.palette.divider}`,
                    background:
                      theme.palette.mode === "light" ? "#f8fafc" : "rgba(255,255,255,0.04)",
                  }}
                >
                  <Typography variant="body2">{safe(ticket.notes)}</Typography>
                </Box>
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>

      {/* Space before fixed footer seam */}
      <Box sx={{ height: 12 }} />
    </Box>
  );
}

